import { useState } from 'react';
import { useCanSeeWarehouses } from '../../store/auth';
import { useQuery } from '@tanstack/react-query';
import { Card, Table, Tag, Typography, Select, DatePicker, Space, Button, Tooltip, theme } from '../../ui';
import { RefreshCw, ArrowUp, ArrowDown } from 'lucide-react';
import type { Dayjs } from 'dayjs';
import { disableFuture } from '../../lib/dateRange';
import { request } from '../../api/client';
import { formatIst } from '../../lib/time';
import { isRedundantSku } from '../../lib/format';
import { PALETTE } from '../../lib/palette';
import { warehouseOption } from '../../lib/warehouseLabel';
import { useServerSort } from '../../hooks/useServerSort';
import { CopyValue } from '../../components/CopyValue';

interface Movement {
  id: string;
  txnDate: string;
  txnType: string;
  direction: 'IN' | 'OUT';
  qty: number;
  prevBalance: number;
  runningBalance: number;
  warehouseId: string;
  warehouse: string;
  productId: string;
  product: string;
  sku: string | null;
  batchId: string | null;
  batchNo: string | null;
  refTable: string;
  refId: string;
  reference: string;
  createdBy: string | null;
  createdByName: string | null;
}

// Human labels for the raw ledger txn types.
const TXN_LABEL: Record<string, string> = {
  GRN: 'Goods Receipt (GRN)',
  SO: 'Sales Dispatch',
  TRANSFER_IN: 'Transfer In',
  TRANSFER_OUT: 'Transfer Out',
  PROD_IN: 'Production In',
  PROD_OUT: 'Production Consume',
  ADJUST_IN: 'Adjustment In',
  ADJUST_OUT: 'Adjustment Out',
  RETURN_IN: 'Sales Return In',
  RETURN_OUT: 'Return Out',
  SAMPLE: 'Sample Issue',
};
const TXN_TYPES = Object.keys(TXN_LABEL);

// What the document behind a movement IS, in the reader's words. The ledger stores a polymorphic
// ref (ref_table + ref_id); naming the table and the internal id tells a warehouse manager nothing,
// so only this label is ever shown.
const REF_DOC: Record<string, string> = {
  orders: 'Order',
  shipments: 'Dispatch',
  stock_transfers: 'Stock transfer',
  stock_adjustments: 'Stock adjustment',
  transfer_discrepancies: 'Transfer count difference',
  grn_headers: 'Goods receipt (GRN)',
  returns: 'Sales return',
  opening: 'Opening stock',
  opening_stock: 'Opening stock',
  stock_batches: 'Opening stock',
};

const fmt = (n: number) => Number(n).toLocaleString('en-IN', { maximumFractionDigits: 3 });

export function StockHistoryPage({ embedded }: { embedded?: boolean } = {}) {
  const canSeeWarehouses = useCanSeeWarehouses();
  const { token } = theme.useToken();
  const [warehouseId, setWarehouseId] = useState<string>();
  const [productId, setProductId] = useState<string>();
  const [txnType, setTxnType] = useState<string>();
  const [range, setRange] = useState<[Dayjs, Dayjs] | null>(null);

  // Filter option sources
  const { data: warehouses } = useQuery({
    queryKey: ['wh-list'],
    queryFn: () => request<any[]>({ url: '/warehouses/my' }),
    // Gated so a role without warehouse access doesn't fire a call it is refused — see useCanSeeWarehouses.
    enabled: canSeeWarehouses,
  });
  const { data: products } = useQuery({
    queryKey: ['catalog-variants'],
    queryFn: () => request<any[]>({ url: '/catalog/with-stock' }),
  });

  // Header clicks drive the backend ORDER BY, not a local re-sort: the endpoint caps the history at
  // 300 rows, so sorting in the browser would only reorder that newest slice rather than the movements
  // the sort is actually asking for (e.g. the largest issue ever, not the largest of the last 300).
  const { sortState, onSortChange, sort } = useServerSort();

  const params: Record<string, string> = {};
  if (warehouseId) params.warehouseId = warehouseId;
  if (productId) params.productId = productId;
  if (txnType) params.txnType = txnType;
  if (range?.[0] && range?.[1]) {
    params.from = range[0].format('YYYY-MM-DD 00:00:00');
    params.to = range[1].format('YYYY-MM-DD 23:59:59');
  }
  if (sort) params.sort = sort;

  const { data, isFetching, refetch } = useQuery({
    queryKey: ['stock-movements', params],
    queryFn: () => request<Movement[]>({ url: '/stock-movements', params }),
  });

  return (
    <div>
      {!embedded && <Typography.Title level={1}>Stock History</Typography.Title>}
      {!embedded && (
        <Typography.Paragraph type="secondary" style={{ marginTop: -8 }}>
          Every stock movement — what changed, in which warehouse, the balance before and after, and who did it.
        </Typography.Paragraph>
      )}

      <Card bodyClassName="pb-2">
        <Space wrap style={{ marginBottom: 12 }}>
          {/* Hidden when the viewer has no warehouse access — an always-empty picker reads as broken. */}
          {canSeeWarehouses && (
            <Select
              allowClear
              showSearch
              optionFilterProp="searchLabel"
              placeholder="All warehouses"
              className="w-55"
              value={warehouseId}
              onChange={setWarehouseId}
              options={(warehouses ?? []).map((w: any) => ({ value: String(w.id), ...warehouseOption(w) }))}
            />
          )}
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="All products"
            className="w-65"
            value={productId}
            onChange={setProductId}
            options={(products ?? []).map((p: any) => ({ value: String(p.productId), label: `${p.name} · ${p.sku}` }))}
          />
          <Select
            allowClear
            placeholder="All movement types"
            className="w-50"
            value={txnType}
            onChange={setTxnType}
            options={TXN_TYPES.map((t) => ({ value: t, label: TXN_LABEL[t] }))}
          />
          <DatePicker.RangePicker disabledDate={disableFuture} value={range as any} onChange={(v) => setRange(v as any)} />
          <Tooltip label="Refresh">
            <Button variant="default" aria-label="Refresh" icon={<RefreshCw size={16} />} onClick={() => refetch()} />
          </Tooltip>
        </Space>

        <Table<Movement>
          rowKey="id"
          size="small"
          loading={isFetching}
          dataSource={data ?? []}
          scroll={{ x: 1100 }}
          sortState={sortState}
          onSortChange={onSortChange}
          pagination={{ defaultPageSize: 20, showSizeChanger: true, showTotal: (t) => `${t} movements` }}
          columns={[
            { title: 'Date / time', dataIndex: 'txnDate', width: 170, sorter: true, render: (v) => formatIst(v) },
            {
              title: 'Warehouse', dataIndex: 'warehouse', width: 190, sorter: true,
              render: (v) => <Tag tone="blue">{v}</Tag>,
            },
            {
              // Capped. Every other column here is pinned, so an unpinned Product took ALL the
              // leftover table width — a stripe of blank space between the product name and the batch.
              title: 'Product', dataIndex: 'product', width: 270, sorter: true,
              render: (v, r) => (
                <div>
                  <div>{v}</div>
                  {r.sku && !isRedundantSku(r.sku, v) && <div style={{ fontSize: 12, color: token.colorTextTertiary }}>{r.sku}</div>}
                </div>
              ),
            },
            // Batch numbers are long ("1783323531398-OPENING-379"): at 110px every one of them wrapped
            // onto a second line, which is what was making these rows double height.
            { title: 'Batch', dataIndex: 'batchNo', width: 265, sorter: true, render: (v) => <span className="whitespace-nowrap">{v ?? '—'}</span> },
            {
              title: 'Movement', dataIndex: 'txnType', width: 170, sorter: true,
              render: (v: string, r) => (
                <Tag tone={r.direction === 'IN' ? 'green' : 'red'}>
                  {r.direction === 'IN' ? <ArrowUp size={16} /> : <ArrowDown size={16} />}
                  {TXN_LABEL[v] ?? v}
                </Tag>
              ),
            },
            {
              title: 'Qty', dataIndex: 'qty', align: 'right' as const, width: 100, sorter: true,
              render: (v: number) => (
                <b style={{ color: v >= 0 ? PALETTE.brand[600] : PALETTE.danger[500] }}>
                  {v >= 0 ? '+' : '−'}{fmt(Math.abs(v))}
                </b>
              ),
            },
            {
              title: 'Prev', dataIndex: 'prevBalance', align: 'right' as const, width: 90, sorter: true,
              render: (v: number) => <span style={{ color: token.colorTextTertiary }}>{fmt(v)}</span>,
            },
            {
              title: 'New', dataIndex: 'runningBalance', align: 'right' as const, width: 90, sorter: true,
              render: (v: number) => <b>{fmt(v)}</b>,
            },
            {
              title: 'Reference', dataIndex: 'reference', width: 150,
              render: (v: string, r) => {
                // The server resolves the ref to its document number when it can, and falls back to a
                // type label when it cannot — a number always carries digits, a label never does, which
                // is what tells the two apart. A movement with no document of its own (a transfer count
                // difference, opening stock) says what it WAS in words; only a real number is worth
                // copying, so a label gets no tooltip of its own.
                if (!v) return <span style={{ color: token.colorTextTertiary }}>—</span>;
                if (!/\d/.test(v)) return <span>{REF_DOC[r.refTable] ?? v}</span>;
                return (
                  <CopyValue value={v} label={REF_DOC[r.refTable] ?? 'Reference'}>
                    <span className="whitespace-nowrap">{v}</span>
                  </CopyValue>
                );
              },
            },
            { title: 'By', dataIndex: 'createdByName', width: 130, sorter: true, render: (v) => v ?? '—' },
          ]}
        />
      </Card>
    </div>
  );
}
